// client/src/components/promoters/PromoterLeaderboard.jsx
//
// /:tenant/:eventId/promoters — every promoter on one event, ranked by
// conversions (then revenue, then clicks), plus the event's promoter rewards
// ("10 tickets sold: free entry"). Growth Phase 2, feature_key "promoters".
//
// API: GET /api/promoters/:tenant/events/:eventId/leaderboard,
//      GET|POST|DELETE /api/promoters/:tenant/events/:eventId/rewards

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome, FaBullhorn } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { delJson, getJson, postJson } from '../../services/http';
import { barPct, formatCount, formatMoney } from '../analytics/attributionFormat';
import '../analytics/PerformancePanel.css';
import './Promoters.css';

export default function PromoterLeaderboard({ tenantId: tenantProp, eventId: eventProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);
  const eventId = Number(eventProp || params.eventId);

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [rewards, setRewards] = useState([]);
  const [form, setForm] = useState({ threshold: '', text: '' });
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [lb, rw] = await Promise.all([
        getJson(`/api/promoters/${tenant}/events/${eventId}/leaderboard`),
        getJson(`/api/promoters/${tenant}/events/${eventId}/rewards`),
      ]);
      setData(lb.data);
      setRewards(rw.data || []);
    } catch (err) {
      setError(/^409/.test(err.message)
        ? t('promoters.notReady', 'Promoters need a database update before they can be used.')
        : t('promoters.leaderboardError', 'Could not load the leaderboard.'));
    }
  }, [tenant, eventId, t]);
  useEffect(() => { load(); }, [load]);

  const addReward = async (e) => {
    e.preventDefault();
    setNotice(null);
    try {
      await postJson(`/api/promoters/${tenant}/events/${eventId}/rewards`, { threshold: Number(form.threshold), text: form.text.trim() });
      setForm({ threshold: '', text: '' });
      load();
    } catch (err) {
      setNotice({ kind: 'error', text: err?.response?.data?.error || t('promoters.actionError', 'That did not work. Try again.') });
    }
  };
  const removeReward = async (id) => {
    try {
      await delJson(`/api/promoters/${tenant}/events/${eventId}/rewards/${id}`);
      load();
    } catch {
      setNotice({ kind: 'error', text: t('promoters.actionError', 'That did not work. Try again.') });
    }
  };

  const rows = data?.promoters || [];
  const maxConv = Math.max(0, ...rows.map((r) => r.conversions));

  const navButtons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(`/${tenant}/${eventId}/hub`) },
    { icon: <FaBullhorn />, label: t('promoters.title', 'Promoters'), onClick: () => navigate(`/${tenant}/promoters`) },
    { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
  ];

  return (
    <div className="pr-page">
      <DesktopActionBar buttons={navButtons} />
      <section className="surface">
        <h2>{t('promoters.leaderboardTitle', 'Promoter leaderboard')}</h2>
        {data?.event && <p className="surface-muted perf-small">{data.event.name}</p>}
      </section>

      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !data ? (
        <LoadingState />
      ) : (
        <>
          <section className="surface">
            {rows.length === 0 ? (
              <div className="note info">
                {t('promoters.noneOnEvent', 'No promoters on this event yet. Assign them from the Promoters page.')}
              </div>
            ) : (
              <div className="perf-table-wrap">
                <table className="perf-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('promoters.promoter', 'Promoter')}</th>
                      <th>{t('performance.conversions', 'Conversions')}</th>
                      <th className="num">{t('performance.clicks', 'Clicks')}</th>
                      <th className="num">{t('performance.revenue', 'Revenue')}</th>
                      <th className="num">{t('promoters.owed', 'Owed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.promoterId}>
                        <td><span className={`pr-rank${r.rank === 1 && r.conversions > 0 ? ' first' : ''}`}>{r.rank}</span></td>
                        <td>
                          <strong>{r.name}</strong> <span className="lm-badge">{r.code}</span>
                          {r.removed && <span className="lm-badge off">{t('promoters.removed', 'Removed')}</span>}
                        </td>
                        <td style={{ minWidth: '8rem' }}>
                          <div className="perf-bar-label">
                            <span className="perf-num">{formatCount(r.conversions)}</span>
                            {r.goalCount ? <span className="perf-num">/ {formatCount(r.goalCount)}</span> : null}
                          </div>
                          <div className="perf-bar" aria-hidden="true">
                            <div className="perf-bar-fill" style={{ width: `${barPct(r.conversions, r.goalCount || maxConv)}%` }} />
                          </div>
                        </td>
                        <td className="num">{formatCount(r.clicks)}</td>
                        <td className="num">{formatMoney(r.revenue)}</td>
                        <td className="num">{formatMoney((r.earnings.pending || 0) + (r.earnings.approved || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="surface">
            <h4>{t('promoters.rewards', 'Rewards')}</h4>
            <p className="surface-muted perf-small">
              {t('promoters.rewardsIntro', 'Promoters on this event see their progress toward each reward.')}
            </p>
            {rewards.length > 0 && (
              <ul className="pr-reward-list">
                {rewards.map((r) => (
                  <li key={r.id}>
                    <span>{t('promoters.rewardLine', '{{count}} conversions: {{text}}', { count: r.threshold, text: r.text })}{r.itemName ? ` (${r.itemName})` : ''}</span>
                    <button type="button" className="lm-btn secondary" onClick={() => removeReward(r.id)}>{t('promoters.unassign', 'Remove')}</button>
                  </li>
                ))}
              </ul>
            )}
            {notice && <div className={`note ${notice.kind === 'ok' ? 'ok' : 'error'}`} role="status">{notice.text}</div>}
            <form className="lm-form" style={{ marginTop: 8 }} onSubmit={addReward}>
              <label>
                {t('promoters.threshold', 'Conversions needed')}
                <input type="number" min="1" step="1" required value={form.threshold} onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))} />
              </label>
              <label>
                {t('promoters.rewardText', 'Reward')}
                <input type="text" maxLength={255} required placeholder={t('promoters.rewardPlaceholder', 'Free entry to the after-party')}
                  value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} />
              </label>
              <button type="submit" className="lm-btn">{t('promoters.addReward', 'Add reward')}</button>
            </form>
          </section>
        </>
      )}

      <AppFooter buttons={navButtons} />
    </div>
  );
}
